import {
  Box,
  HStack,
  Icon,
  Input,
  Text,
  VStack,
  useOutsideClick,
} from "@chakra-ui/react";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import {
  CountryCode,
  parsePhoneNumber,
  parsePhoneNumberFromString,
} from "libphonenumber-js";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import ReactCountryFlag from "react-country-flag";
import { getCountryDisplayName } from "../helpers/country-display";
import { countries as countriesData } from "../helpers/countries";

type Props = {
  id: string;
  onChange: (value: string) => void;
  defaultCountryCode?: string;
  value?: string;
};

type CountryOption = {
  code: string;
  dialCode: string;
  label: string;
};

const normalizeDialCode = (dialCode: string) => dialCode.replace(/\s/g, "");
const normalizeSearchValue = (value: string) =>
  value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();

export function PhoneInput({
  id,
  onChange,
  defaultCountryCode = "ES",
  value,
}: Props) {
  const { i18n, t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [number, setNumber] = useState("");
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const didSyncRef = useRef(false);

  const countryOptions = useMemo<CountryOption[]>(() => {
    const language = i18n.language.startsWith("en") ? "en" : "es";
    return countriesData
      .map((country) => ({
        code: country.code,
        dialCode: normalizeDialCode(country.dial_code),
        label: getCountryDisplayName(country, language),
      }))
      .sort((a, b) => a.label.localeCompare(b.label, i18n.language));
  }, [i18n.language]);

  const [selectedCode, setSelectedCode] = useState(defaultCountryCode);

  useEffect(() => {
    if (!countryOptions.some((country) => country.code === selectedCode)) {
      setSelectedCode(defaultCountryCode);
    }
  }, [countryOptions, defaultCountryCode, selectedCode]);

  const selectedCountry =
    countryOptions.find((country) => country.code === selectedCode) ??
    countryOptions[0];

  const filteredOptions = useMemo(() => {
    const needle = normalizeSearchValue(query).trim();
    if (!needle) return countryOptions;
    return countryOptions.filter((country) => {
      const dial = country.dialCode.toLowerCase();
      const label = normalizeSearchValue(country.label);
      return (
        label.includes(needle) ||
        country.code.toLowerCase().includes(needle) ||
        dial.includes(needle.replace(/\s/g, ""))
      );
    });
  }, [countryOptions, query]);

  useEffect(() => {
    if (!selectedCountry) return;
    const trimmed = number.replace(/\s/g, "");
    if (!trimmed) {
      onChange("");
      return;
    }
    try {
      const countryCode = selectedCountry.code as CountryCode;
      const phoneNumber = parsePhoneNumber(trimmed, countryCode).number;
      if (phoneNumber) {
        onChange(phoneNumber);
        return;
      }
    } catch (error) {
      // fallback below
    }
    const cleaned = trimmed.startsWith("+") ? trimmed.slice(1) : trimmed;
    onChange(`${selectedCountry.dialCode}${cleaned}`);
  }, [number, onChange, selectedCountry]);

  useOutsideClick({
    ref: dropdownRef,
    handler: () => setIsOpen(false),
  });

  useEffect(() => {
    if (didSyncRef.current) return;
    const trimmed = (value ?? "").replace(/\s/g, "");
    if (!trimmed) {
      didSyncRef.current = true;
      return;
    }
    const normalizedTrimmed = trimmed.startsWith("+") ? trimmed : `+${trimmed}`;
    const dialCodes = new Set(
      countryOptions.map((country) => country.dialCode)
    );
    if (dialCodes.has(trimmed) || dialCodes.has(normalizedTrimmed)) {
      setNumber("");
      didSyncRef.current = true;
      return;
    }
    const parsed = parsePhoneNumberFromString(trimmed);
    if (
      parsed?.country &&
      countryOptions.some((country) => country.code === parsed.country)
    ) {
      setSelectedCode(parsed.country);
      setNumber(parsed.nationalNumber);
      didSyncRef.current = true;
      return;
    }
    setNumber(trimmed);
    didSyncRef.current = true;
  }, [countryOptions, value]);

  return (
    <HStack spacing={2} w="full">
      <Box minW="120px" position="relative" ref={dropdownRef}>
        <Box
          as="button"
          type="button"
          w="full"
          borderWidth="1.5px"
          borderColor="slate.300"
          borderRadius="16px"
          bg="white"
          px={3}
          py={2}
          h="48px"
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          onClick={() => setIsOpen((prev) => !prev)}
          _hover={{ borderColor: "slate.400" }}
          _focusVisible={{
            borderColor: "#13192E",
            boxShadow: "0 0 0 3px rgba(19, 25, 46, 0.06)",
          }}
        >
          <HStack spacing={2} minW={0}>
            {selectedCountry && (
              <ReactCountryFlag
                countryCode={selectedCountry.code}
                svg
                style={{ width: "1.2em", height: "1.2em" }}
                aria-label={selectedCountry.label}
              />
            )}
            <Text color="slate.600" fontWeight={600} fontSize="16px">
              {selectedCountry?.dialCode}
            </Text>
          </HStack>
          <Icon boxSize={4} color="slate.400">
            <ChevronDownIcon />
          </Icon>
        </Box>

        {isOpen && (
          <Box
            position="absolute"
            zIndex={10}
            mt={2}
            w={{ base: "320px", md: "360px" }}
            maxW="90vw"
            borderWidth="1px"
            borderColor="slate.200"
            bg="white"
            borderRadius="12px"
            boxShadow="lg"
            overflow="hidden"
          >
            <Box p={3} borderBottomWidth={1} borderColor="slate.100">
              <Input
                borderRadius="10px"
                placeholder={t("personalDetails.form.phoneSearchPlaceholder")}
                value={query}
                onChange={(event) => setQuery(event.target.value)}
              />
            </Box>
            <Box maxH="240px" overflowY="auto">
              {filteredOptions.map((country) => (
                <Box
                  key={country.code}
                  px={4}
                  py={3}
                  cursor="pointer"
                  _hover={{ bg: "slate.50" }}
                  onClick={() => {
                    setSelectedCode(country.code);
                    setQuery("");
                    setIsOpen(false);
                  }}
                >
                  <HStack spacing={3}>
                    <ReactCountryFlag
                      countryCode={country.code}
                      svg
                      style={{ width: "1.2em", height: "1.2em" }}
                      aria-label={country.label}
                    />
                    <VStack align="start" spacing={0} minW={0}>
                      <Text fontWeight={500} fontSize="16px">{country.label}</Text>
                      <Text fontSize="14px" color="slate.500">
                        {country.code} {country.dialCode}
                      </Text>
                    </VStack>
                  </HStack>
                </Box>
              ))}
              {filteredOptions.length === 0 && (
                <Text px={4} py={3} color="slate.500" fontSize="14px">
                  {t("personalDetails.form.phoneNoResults")}
                </Text>
              )}
            </Box>
          </Box>
        )}
      </Box>

      <Box w="full">
        <Input
          id={id}
          onChange={(event) => setNumber(event.target.value)}
          value={number}
          inputMode="tel"
        />
      </Box>
    </HStack>
  );
}
